import { Box, Typography, Card, CardContent, IconButton, List, ListItem, ListItemText, ListItemSecondaryAction, Button } from '@mui/material';
import { Close } from '@mui/icons-material';
import React, { useEffect, useMemo, useState } from 'react';
import { useSymbol } from '@/contexts/SymbolContext';
import SymbolSelector from '../symbolSelector';

interface SymbolBlocksProps {
  initialBlocks?: string[]; // Just symbol IDs
  onChange?: (symbolIds: string[]) => void;
}

const SymbolBlocks: React.FC<SymbolBlocksProps> = ({ initialBlocks = [], onChange }) => {
  const [blockedSymbolIds, setBlockedSymbolIds] = useState<string[]>(initialBlocks);
  const [selectedSymbol, setSelectedSymbol] = useState<string | null>(null);

  const { getContractByProductId, getContractByProductName } = useSymbol();

  // Update state when initialBlocks changes
  useEffect(() => {
    setBlockedSymbolIds(initialBlocks);
  }, [initialBlocks]);

  // Notify parent when blocked symbols change
  useEffect(() => {
    if (onChange) {
      onChange(blockedSymbolIds);
    }
  }, [blockedSymbolIds, onChange]);

  const addSymbolBlock = () => {
    if (!selectedSymbol) return;

    const contract = getContractByProductName(selectedSymbol);
    if (!contract) return;

    // Check if symbol is already blocked
    if (blockedSymbolIds.includes(contract.productId)) {
      return;
    }

    setBlockedSymbolIds([...blockedSymbolIds, contract.productId]);
    setSelectedSymbol(null);
  };

  const removeSymbolBlock = (symbolId: string) => {
    setBlockedSymbolIds(blockedSymbolIds.filter((id) => id !== symbolId));
  };

  const getSymbolName = (symbolId: string) => {
    const contract = getContractByProductId(symbolId);
    if (!contract) {
      return 'Unknown';
    }

    return (
      <span>
        {contract.productName}
        <span style={{ color: 'grey', marginLeft: '1em' }}>({contract.description})</span>
      </span>
    );
  };

  const selector = useMemo(() => {
    return <SymbolSelector selectedSymbol={selectedSymbol} onSymbolChange={setSelectedSymbol} doNotInclude={blockedSymbolIds} />;
  }, [selectedSymbol, blockedSymbolIds]);

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Card variant='outlined' sx={{ mb: 0 }}>
        <CardContent sx={{ padding: '1.2em', paddingBottom: '1em !important' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexDirection: 'column',
              width: '100%'
            }}
          >
            <Box sx={{ flexGrow: 1, width: '100%' }}>{selector}</Box>
            <Button style={{ width: '100%' }} variant='contained' color='primary' onClick={addSymbolBlock} disabled={!selectedSymbol}>
              Add Block
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Typography variant='subtitle1' style={{ color: '#fff', marginTop: 2 }}>
        Current Symbol Blocks
      </Typography>

      {blockedSymbolIds.length === 0 ? (
        <Card variant='outlined'>
          <CardContent sx={{ padding: '1em', textAlign: 'center' }}>
            <Typography color='textSecondary'>No symbols are currently blocked</Typography>
          </CardContent>
        </Card>
      ) : (
        <List sx={{ maxHeight: '200px', overflow: 'auto' }}>
          {blockedSymbolIds.map((symbolId) => (
            <Card key={symbolId} variant='outlined' sx={{ mb: 1 }}>
              <ListItem>
                <ListItemText primary={getSymbolName(symbolId)} />
                <ListItemSecondaryAction>
                  <IconButton edge='end' aria-label='delete' onClick={() => removeSymbolBlock(symbolId)}>
                    <Close />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            </Card>
          ))}
        </List>
      )}
    </Box>
  );
};

export default SymbolBlocks;
